import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Play, Upload, Database, RefreshCw, CheckCircle2, XCircle, Zap, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function DataSourcesPage() {
  const [adapters, setAdapters] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [running, setRunning] = useState(null);
  const [attomUsage, setAttomUsage] = useState(null);
  const [attomZips, setAttomZips] = useState([]);
  const [syncZip, setSyncZip] = useState("");
  const [batchRunning, setBatchRunning] = useState(false);

  const load = () => {
    api.get("/admin/ingestion/adapters").then((r) => setAdapters(r.data.items || []));
    api.get("/admin/ingestion/jobs").then((r) => setJobs(r.data.items || []));
    api.get("/admin/attom/usage").then((r) => setAttomUsage(r.data)).catch(() => {});
    api.get("/admin/attom/target-zips").then((r) => setAttomZips(r.data.zips || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const runAdapter = async (key) => {
    setRunning(key);
    try {
      const r = await api.post("/admin/ingestion/run", { adapter: key, params: { count: 8 } });
      toast.success(`${key}: ${r.data.count} new records ingested`);
      load();
    } catch (e) { toast.error("Ingestion failed"); } finally { setRunning(null); }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/ingestion/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`CSV: imported ${r.data.count} records`);
      load();
    } catch (e) { toast.error("CSV import failed"); }
    e.target.value = "";
  };

  const runAttomZip = async () => {
    const z = syncZip.trim();
    if (!/^\d{5}$/.test(z)) { toast.error("Enter a 5-digit ZIP"); return; }
    setRunning("attom_zip");
    try {
      const r = await api.post("/admin/attom/sync-zip", { zip: z });
      const msg = r.data.error ? `${z}: ${r.data.error}` : `${z}: +${r.data.count} new, ${r.data.updated || 0} updated`;
      r.data.error ? toast.error(msg) : toast.success(msg);
      setSyncZip("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "ATTOM sync failed");
    } finally { setRunning(null); }
  };

  const runAttomBatch = async () => {
    if (!window.confirm(`This will consume ~${attomZips.length} ATTOM API calls (${attomZips.join(", ")}). Continue?`)) return;
    setBatchRunning(true);
    try {
      const r = await api.post("/admin/attom/sync-target-zips", {});
      toast.success(`Batch complete — ${r.data.inserted} new, ${r.data.updated} updated across ${r.data.target_zips.length} ZIPs`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Batch failed");
    } finally { setBatchRunning(false); }
  };

  return (
    <div className="space-y-6" data-testid="data-sources-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] mb-2 font-semibold">Data Sources</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Ingestion Adapters & Jobs</h1>
        <p className="text-[13px] text-[#b8ac96] mt-2 max-w-2xl">Run the pluggable adapters to fetch new distressed-opportunity records. Bulk-import via CSV. Real production feeds plug in behind these interfaces.</p>
      </div>

      {/* ATTOM Panel */}
      <div className="border border-[#d4a574]/40 rounded-lg bg-gradient-to-br from-[#241d16] to-[#1c1712] p-5" data-testid="attom-panel">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded bg-[#d4a574]/10 border border-[#d4a574]/40 flex items-center justify-center">
              <Zap size={18} className="text-[#d4a574]" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold">ATTOM Data API · Live Preforeclosures</div>
              <div className="text-[11px] text-[#7a6d5a] font-mono">160M+ properties · nationwide · free-trial tier</div>
            </div>
          </div>
          {attomUsage && (
            <div className="text-right" data-testid="attom-usage">
              <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a]">This Month</div>
              <div className="font-mono text-[18px] font-bold text-[#d4a574]">{attomUsage.used} / {attomUsage.quota}</div>
              <div className="w-40 h-1.5 rounded bg-[#33291f] overflow-hidden mt-1">
                <div className="h-full bg-[#d4a574]" style={{ width: `${Math.min(100, attomUsage.pct)}%` }} />
              </div>
              <div className="text-[10px] text-[#7a6d5a] mt-1">{attomUsage.remaining} calls remaining</div>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-4 rounded bg-[#14100c] border border-[#33291f]">
            <div className="text-[11px] uppercase tracking-wider text-[#b8ac96] mb-2 font-semibold">Sync a specific ZIP</div>
            <div className="flex gap-2">
              <input
                value={syncZip}
                onChange={(e) => setSyncZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="e.g. 90210"
                data-testid="attom-zip-input"
                className="flex-1 bg-[#1c1712] border border-[#33291f] rounded px-3 py-2 text-[13px] font-mono focus:outline-none focus:border-[#d4a574]"
              />
              <button
                onClick={runAttomZip}
                disabled={running === "attom_zip" || !syncZip}
                data-testid="attom-sync-zip-btn"
                className="px-4 py-2 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[12px] hover:bg-[#e8b988] disabled:opacity-50 flex items-center gap-1.5"
              >
                {running === "attom_zip" ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
                Sync ZIP
              </button>
            </div>
            <div className="text-[10px] text-[#7a6d5a] mt-2 font-mono">Costs 1 ATTOM call · returns up to 25 preforeclosures</div>
          </div>

          <div className="p-4 rounded bg-[#14100c] border border-[#33291f]">
            <div className="text-[11px] uppercase tracking-wider text-[#b8ac96] mb-2 font-semibold">Batch — target ZIPs ({attomZips.length})</div>
            <div className="text-[10px] font-mono text-[#7a6d5a] mb-3 truncate" title={attomZips.join(", ")}>{attomZips.join(", ") || "—"}</div>
            <button
              onClick={runAttomBatch}
              disabled={batchRunning || !attomZips.length}
              data-testid="attom-batch-btn"
              className="w-full px-4 py-2 rounded border border-[#4a3a2b] text-[#d4a574] font-semibold text-[12px] hover:border-[#d4a574] disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {batchRunning ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
              Run batch sync
            </button>
            <div className="text-[10px] text-[#7a6d5a] mt-2 font-mono">Also runs automatically nightly at 03:00 UTC</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {adapters.map((a) => (
          <div key={a.key} className="border border-[#33291f] rounded-lg p-5 bg-[#1c1712]">
            <Database size={18} className="text-[#d4a574] mb-3" />
            <div className="font-display text-lg font-semibold">{a.label}</div>
            <div className="text-[11px] font-mono text-[#7a6d5a] mb-4">source: {a.source}</div>
            <button onClick={() => runAdapter(a.key)} disabled={running === a.key} data-testid={`run-${a.key}`} className="flex items-center gap-2 px-3 py-2 rounded bg-[#241d16] border border-[#4a3a2b] hover:border-[#d4a574] text-[12px] text-[#d4a574] disabled:opacity-50">
              {running === a.key ? <RefreshCw size={13} className="animate-spin" /> : <Play size={13} />} Run adapter
            </button>
          </div>
        ))}
        <label className="border border-dashed border-[#33291f] rounded-lg p-5 bg-[#1c1712] hover:border-[#d4a574] cursor-pointer flex flex-col">
          <Upload size={18} className="text-[#d4a574] mb-3" />
          <div className="font-display text-lg font-semibold">CSV Bulk Import</div>
          <div className="text-[11px] text-[#7a6d5a] mb-4">Cols: category,title,address,city,state,zip,latitude,longitude</div>
          <input type="file" accept=".csv" onChange={uploadCsv} data-testid="csv-upload" className="text-[11px] text-[#b8ac96]" />
        </label>
      </div>

      <div className="border border-[#33291f] rounded-lg bg-[#1c1712] overflow-hidden">
        <div className="p-4 border-b border-[#33291f] font-display text-lg font-semibold">Recent Ingestion Jobs</div>
        <table className="w-full text-[13px]" data-testid="jobs-table">
          <thead className="bg-[#241d16] text-[10px] uppercase tracking-wider text-[#7a6d5a]">
            <tr>
              <th className="text-left px-4 py-2">Adapter</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Records</th>
              <th className="text-left px-4 py-2">Started</th>
              <th className="text-left px-4 py-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} className="border-t border-[#33291f]">
                <td className="px-4 py-2 font-mono text-[12px]">{j.adapter}{j.zip ? ` (${j.zip})` : ""}</td>
                <td className="px-4 py-2">
                  {j.status === "success" ? <span className="flex items-center gap-1 text-[#34d399]"><CheckCircle2 size={12} /> success</span> : <span className="flex items-center gap-1 text-[#f87171]"><XCircle size={12} /> error</span>}
                </td>
                <td className="px-4 py-2 font-mono text-[#d4a574]">{j.count}{j.updated ? ` (+${j.updated} upd)` : ""}</td>
                <td className="px-4 py-2 font-mono text-[11px] text-[#7a6d5a]">{j.started_at?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-2 text-[11px] text-[#b8ac96]">{j.error || j.filename || "—"}</td>
              </tr>
            ))}
            {jobs.length === 0 && <tr><td colSpan={5} className="text-center text-[#7a6d5a] py-8 text-sm">No jobs yet. Run an adapter above.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
